import React, { useState, useEffect, useContext } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Menu,
  MenuItem,
  IconButton,
  Badge,
  useTheme,
  Container,
} from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  AdminPanelSettings,
  Notifications,
  Person,
  Brightness4,
  Brightness7,
  Menu as MenuIcon,
} from "@mui/icons-material";
import { useAuth } from "../../hooks/useAuth";
import { categoryService } from "../../api/services";
import logo from "../../assets/logo.png";
import { ColorModeContext } from "../../contexts/ThemeContext";

const HeaderContent = ({ position = "sticky", elevation = 0, sx = {} }) => {
  const { isAuthenticated, logout, user } = useAuth();
  const colorMode = useContext(ColorModeContext);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [categories, setCategories] = useState([]);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Estado para controlar visibilidad del header
  const [showHeader, setShowHeader] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    fetchFeaturedCategories();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scroll hacia abajo
        setShowHeader(false);
      } else {
        // Scroll hacia arriba
        setShowHeader(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const fetchFeaturedCategories = async () => {
    try {
      const response = await categoryService.getCategories();
      const featured =
        response.data
          ?.filter((cat) => cat.is_featured)
          ?.sort((a, b) => a.order - b.order) || [];
      setCategories(featured);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    setUserMenuAnchor(null);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const isAdmin = user?.profile?.role === "admin" || user?.is_superuser;
  const isEditor = user?.profile?.role === "editor";
  const canAccessAdmin = isAdmin || isEditor;

  const navItems = [
    { label: "Inicio", path: "/" },
    { label: "Blog", path: "/blog" },
    ...categories.map(cat => ({ label: cat.name, path: `/category/${cat.slug}` })).slice(0, 3)
  ];

  const isDark = theme.palette.mode === 'dark';

  return (
    <header
      style={{
        position: 'fixed',
        top: showHeader ? 0 : '-100px',
        left: 0,
        right: 0,
        zIndex: 1200,
        background: isDark ? 'rgba(13,13,26,.95)' : 'rgba(245,247,250,.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: `1px solid ${isDark ? '#00F5FF' : '#FF3FA3'}`,
        transition: 'top 0.3s ease-in-out',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ height: 70 }}>
          <Box
            className="neon-blink"
            component={Link}
            to="/"
            sx={{
              textDecoration: 'none',
              fontFamily: 'Orbitron, sans-serif',
              fontSize: '1.8rem',
              fontWeight: 800,
              background: `linear-gradient(135deg,${isDark ? '#00F5FF' : '#FF3FA3'},${isDark ? '#FF00C1' : '#00CFFF'})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: `0 0 10px ${isDark ? '#00F5FF' : '#FF3FA3'}`,
              mr: 4,
            }}
          >
            V.D
          </Box>

          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1, flexGrow: 1 }}>
            {navItems.map((item) => (
              <Button
                key={item.path}
                component={Link}
                to={item.path}
                sx={{
                  color: location.pathname === item.path ? 'primary.main' : 'text.primary',
                  fontWeight: location.pathname === item.path ? 700 : 500,
                  position: 'relative',
                  '&::after': location.pathname === item.path ? {
                    content: '""',
                    position: 'absolute',
                    bottom: 6,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '4px',
                    height: '4px',
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                  } : {},
                  '&:hover': {
                    backgroundColor: 'rgba(99, 102, 241, 0.08)',
                  }
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <IconButton onClick={colorMode.toggleColorMode} sx={{ color: isDark ? '#00F5FF' : '#FF3FA3' }}>
              {isDark ? <Brightness7 /> : <Brightness4 />}
            </IconButton>

            {isAuthenticated ? (
              <>
                {canAccessAdmin && (
                  <Button component={Link} to="/admin" variant="outlined" sx={{ borderColor: '#00F5FF', color: '#00F5FF', '&:hover': { borderColor: '#FF00C1', color: '#FF00C1' } }}>
                    Admin
                  </Button>
                )}

                <IconButton
                  color="inherit"
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '12px',
                  }}
                >
                  <Badge badgeContent={0} color="secondary" variant="dot">
                    <Notifications fontSize="small" />
                  </Badge>
                </IconButton>

                <Button
                  onClick={handleUserMenuOpen}
                  startIcon={<Person />}
                  sx={{
                    textTransform: "none",
                    color: 'text.primary',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '12px',
                    px: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(99, 102, 241, 0.08)',
                      borderColor: 'primary.main',
                    }
                  }}
                >
                  <Box sx={{ display: { xs: "none", sm: "block" }, textAlign: 'left' }}>
                    <Box sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      {user?.firstName || 'Usuario'}
                    </Box>
                  </Box>
                </Button>

                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={handleUserMenuClose}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      mt: 1.5,
                      overflow: 'visible',
                      filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                      borderRadius: '12px',
                      border: `1px solid ${theme.palette.divider}`,
                      '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        borderLeft: `1px solid ${theme.palette.divider}`,
                      },
                    },
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <MenuItem component={Link} to="/profile" onClick={handleUserMenuClose}>
                    Mi Perfil
                  </MenuItem>
                  {canAccessAdmin && (
                    <MenuItem component={Link} to="/admin" onClick={handleUserMenuClose}>
                      Panel de Administración
                    </MenuItem>
                  )}
                  <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                    Cerrar Sesión
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button component={Link} to="/login" variant="text" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  Login
                </Button>
                <Button component={Link} to="/login?tab=register" variant="contained" color="primary" sx={{ boxShadow: '0 4px 14px 0 rgba(99, 102, 241, 0.39)' }}>
                  Registro
                </Button>
              </Box>
            )}
          </Box>
        </Toolbar>
      </Container>
    </header>
  );
};

export default HeaderContent;